<script lang="ts">
  import { onMount } from "svelte";
  import { push, link } from "svelte-spa-router";
  import {
    login,
    requestVerify,
    ApiError,
    e2eeStatus,
    type ActiveSession,
    type SessionLimitBody,
  } from "../lib/api";
  import { setUser, setError, session } from "../lib/session.svelte";
  import { setLocale, t, _ } from "../lib/i18n";
  import { provisionIdentity, loadSharedKeys } from "../lib/e2eeSession";
  import { setProvisionFailed, getProvisionFailed } from "../lib/e2eeProvisionState";
  import { hasPendingReturnRoom } from "../lib/e2eeRecoveryReturn";

  // Max time login waits for E2EE identity provisioning before proceeding to
  // /class anyway. A slow/failed provision must never block login; the in-class
  // readiness gate + plaintext fallback handle the not-ready case.
  const PROVISION_TIMEOUT_MS = 6000;

  const EMAIL_KEY = "student-last-email";

  let email = "";
  let password = "";
  /**
   * Opt-in long-lived (7d idle, 90d max) session. Deliberately NOT persisted:
   * on a shared device the next student would find the box pre-ticked and
   * silently get a 7d session. Always starts unchecked, every load.
   */
  let remember = false;
  let submitting = false;
  let localError: string | null = null;
  let needsVerify = false;
  let resendState: "idle" | "sending" | "sent" = "idle";

  // E2EE repair mode: Class bounced us here because the in-memory identity is
  // missing and re-provisioning needs the password (only Login has it). A
  // pending return room is the signal. We DON'T clear it here — Class consumes
  // it one-shot via takePendingReturnRoom() after we navigate back to /class.
  let repairMode = hasPendingReturnRoom();
  // Set when provisioning fails/times out during a repair, to surface a
  // non-blocking inline notice (login itself still succeeds).
  let repairProvisionFailed = false;

  // ---- Concurrent-session limit (HTTP 409 `session_limit`) ----
  // When the backend rejects login because too many sessions are active, it
  // returns the live list. We show a modal letting the student kill enough of
  // them to free a slot, then retry the SAME login with kill_session_ids.
  let sessionLimitOpen = false;
  let activeSessions: ActiveSession[] = [];
  let maxSessions = 0;
  // Set of session ids the student selected to kill.
  let killSelected = new Set<string>();

  // How many sessions MUST be killed to get under the limit and free one slot:
  //   needed = active - max + 1  (clamped to >= 1 while the modal is open).
  $: neededKills = Math.max(1, activeSessions.length - maxSessions + 1);
  // Confirm is enabled once enough slots are freed (and not mid-request).
  $: canConfirmKill = killSelected.size >= neededKills && !submitting;

  /** Humanize an age in ms into "2h 14m" / "5m" / "just now"; null ⇒ unknown. */
  function humanizeAge(ageMs: number | null): string {
    if (ageMs == null) return t("login.sessionLimit.unknownAge");
    const totalMin = Math.floor(ageMs / 60000);
    if (totalMin < 1) return t("login.sessionLimit.justNow");
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
    return `${m}m`;
  }

  /** Build the small muted metadata line for one session row. */
  function sessionMeta(s: ActiveSession): string {
    const device =
      !s.device || s.device === "Unknown"
        ? t("login.sessionLimit.unknownDevice")
        : s.device;
    const browser =
      !s.browser || s.browser === "Unknown"
        ? t("login.sessionLimit.unknownBrowser")
        : s.browser;
    const ip = s.ip ?? t("login.sessionLimit.unknownIp");
    const active = t("login.sessionLimit.activeFor", {
      age: humanizeAge(s.age_ms),
    });
    return `${device} · ${browser} · ${ip} · ${active}`;
  }

  function toggleKill(id: string) {
    const next = new Set(killSelected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    killSelected = next;
  }

  function cancelSessionLimit() {
    sessionLimitOpen = false;
    activeSessions = [];
    killSelected = new Set();
    submitting = false;
  }

  /** Open (or refresh, on a repeat 409) the modal from a session_limit body. */
  function openSessionLimit(body: SessionLimitBody) {
    activeSessions = body.active_sessions ?? [];
    maxSessions = body.max_sessions;
    // Preserve any still-valid prior selections (server list may have changed).
    const live = new Set(activeSessions.map((s) => s.id));
    const kept = new Set([...killSelected].filter((id) => live.has(id)));
    // Convenience: single-session-over-limit ⇒ pre-select the obvious target.
    if (maxSessions === 1 && activeSessions.length === 1) {
      kept.add(activeSessions[0].id);
    }
    killSelected = kept;
    sessionLimitOpen = true;
  }

  /** Confirm: retry login killing the selected sessions. */
  function confirmKill() {
    if (!canConfirmKill) return;
    void doLogin([...killSelected]);
  }

  onMount(() => {
    try {
      const saved = localStorage.getItem(EMAIL_KEY);
      if (saved) email = saved;
    } catch {}
  });

  /** Narrow an unknown ApiError body to a `session_limit` 409 payload. */
  function asSessionLimit(err: unknown): SessionLimitBody | null {
    if (!(err instanceof ApiError) || err.status !== 409) return null;
    const b = err.body;
    if (
      b &&
      typeof b === "object" &&
      (b as Record<string, unknown>).error === "session_limit"
    ) {
      return b as SessionLimitBody;
    }
    return null;
  }

  /**
   * Perform the login request and run the shared post-login flow. Used by both
   * the initial submit and the post-kill retry from the session-limit modal.
   *
   * - On success: setUser → provision E2EE (timed) → setLocale → persist email →
   *   navigate to /class (identical to the original submit success path).
   * - On a `session_limit` 409: open/refresh the modal instead of erroring.
   * - On email_not_verified / 401 / other: existing inline error handling.
   *
   * @param killIds session ids to free on a retry; omitted on the first attempt.
   */
  async function doLogin(killIds?: string[]): Promise<void> {
    localError = null;
    needsVerify = false;
    resendState = "idle";
    repairProvisionFailed = false;
    setError(null);
    if (!email || !password) {
      localError = t("login.error.required");
      return;
    }
    submitting = true;
    // Capture repair state up front: navigation back to /class depends on it,
    // and the pending room must survive (Class clears it one-shot), so we only
    // read — never consume — it here.
    const wasRepair = hasPendingReturnRoom();
    try {
      const res = await login(
        killIds && killIds.length > 0
          ? { email, password, remember, kill_session_ids: killIds }
          : { email, password, remember },
      );
      // Success: the limit modal (if it was open) is now obsolete.
      sessionLimitOpen = false;
      setUser({
        email: res.email,
        display_name: res.display_name,
        full_name: res.full_name ?? null,
        roles: res.roles,
        language: res.language,
        demo: res.demo === true,
        must_change_password: res.must_change_password === true,
      });
      // E2EE (Phase 2): when the feature is on, provision/load the student's
      // identity using the just-entered password (the ONLY moment we have it),
      // then decrypt any stored media keys. We AWAIT this (with a short timeout)
      // so the identity is ready on class entry — a QR scan there needs it. A
      // slow/failed provision must NOT block login forever, so after the timeout
      // we proceed to /class anyway (the in-class readiness gate + plaintext
      // fallback cover the not-ready case). Errors are recorded (not swallowed
      // silently) but never hard-block login.
      await provisionE2eeWithTimeout(password, PROVISION_TIMEOUT_MS);
      // E2EE repair visibility: if provisioning failed/timed out during a repair
      // login, surface a non-blocking inline notice instead of silently bouncing
      // back. Login still succeeds; the student can simply try again.
      if (wasRepair && getProvisionFailed()) {
        repairProvisionFailed = true;
        return;
      }
      // Switch the UI to the student's configured language immediately.
      setLocale(res.language);
      try {
        localStorage.setItem(EMAIL_KEY, email);
      } catch {}
      // Normal and repair success both land on /class. In repair mode Class
      // reads the stashed room (takePendingReturnRoom) and auto-rejoins.
      push("/class");
    } catch (err) {
      // Concurrent-session limit: open or refresh the modal (don't stack).
      const limit = asSessionLimit(err);
      if (limit) {
        openSessionLimit(limit);
        return;
      }
      if (err instanceof ApiError && err.message === "email_not_verified") {
        needsVerify = true;
        localError = t("login.notVerified");
        setError(localError);
        return;
      }
      const msg =
        err instanceof ApiError
          ? err.status === 401
            ? t("login.error.invalid")
            : err.message
          : t("login.error.failed");
      localError = msg;
      setError(msg);
    } finally {
      submitting = false;
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    await doLogin();
  }

  async function resend() {
    resendState = "sending";
    try {
      await requestVerify({ email });
    } catch {
      /* neutral result regardless */
    }
    resendState = "sent";
  }

  /**
   * Provision (or load) the E2EE identity with the login password, then decrypt
   * stored media keys. Gated on the server flag. The password stays local to
   * this call. A failure is recorded via {@link setProvisionFailed} so the class
   * view can react (readiness gate / plaintext fallback) instead of silently
   * proceeding with a null identity.
   */
  async function provisionE2ee(pw: string): Promise<void> {
    try {
      const st = await e2eeStatus();
      if (st.enabled !== true) {
        setProvisionFailed(false);
        return;
      }
      await provisionIdentity(pw);
      await loadSharedKeys();
      setProvisionFailed(false);
    } catch {
      // Non-fatal: record the failure so the class view surfaces the
      // not-ready state + fallback. Identity can still recover or downgrade.
      setProvisionFailed(true);
      throw new Error("e2ee_provision_failed");
    }
  }

  /**
   * Run {@link provisionE2ee} but resolve after `ms` no matter what, so a slow
   * or hung provision never blocks login. On timeout we flag the failure (the
   * underlying provision keeps running; the class view's gate + fallback cover
   * the gap) and let login proceed to /class.
   */
  async function provisionE2eeWithTimeout(pw: string, ms: number): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(() => {
        setProvisionFailed(true);
        resolve();
      }, ms);
    });
    try {
      await Promise.race([
        provisionE2ee(pw).catch(() => {
          /* recorded in provisionE2ee; never block login */
        }),
        timeout,
      ]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
</script>

<section class="login">
  <div class="card">
    <h1>{$_("login.title")}</h1>
    <p class="sub">{$_("login.subtitle")}</p>
    {#if repairMode}
      <!-- E2EE repair notice: explain WHY the student is back at Login and that
           they'll auto-return to the classroom after re-entering the password. -->
      <div class="repair-notice" data-testid="login-repair-notice">
        <strong>{$_("e2ee.keyMissing.relogin")}</strong>
        <p>{$_("e2ee.keyMissing.reloginDetail")}</p>
      </div>
    {/if}
    <form on:submit={submit}>
      <label>
        {$_("login.username")}
        <input
          type="email"
          autocomplete="username"
          bind:value={email}
          data-testid="username"
          required
        />
      </label>
      <label>
        {$_("login.password")}
        <input
          type="password"
          autocomplete="current-password"
          bind:value={password}
          data-testid="password"
          required
        />
      </label>
      <label class="remember">
        <input type="checkbox" bind:checked={remember} data-testid="remember-me" />
        <span>
          {$_("login.rememberMe")}
          <small>{$_("login.rememberMeHint")}</small>
        </span>
      </label>
      <button type="submit" disabled={submitting} data-testid="submit">
        {submitting ? $_("login.signingIn") : $_("login.signIn")}
      </button>
      {#if localError}
        <p class="error" data-testid="error">{localError}</p>
      {:else if $session.error}
        <p class="error">{$session.error}</p>
      {/if}
      {#if repairProvisionFailed}
        <!-- Non-blocking: login succeeded but E2EE provisioning didn't complete.
             Invite a retry without leaving the page. -->
        <p class="error" data-testid="login-repair-failed">
          {$_("e2ee.keyMissing.detail")}
        </p>
      {/if}
      {#if needsVerify}
        {#if resendState === "sent"}
          <p class="sub" data-testid="resend-sent">{$_("login.resent")}</p>
        {:else}
          <button
            type="button"
            class="text-link"
            on:click={resend}
            disabled={resendState === "sending"}
            data-testid="resend-verification"
          >
            {resendState === "sending" ? $_("login.resending") : $_("login.resend")}
          </button>
        {/if}
      {/if}
    </form>
    <a class="forgot" href="/reset" use:link data-testid="forgot-password">
      {$_("login.forgot")}
    </a>
  </div>
</section>

{#if sessionLimitOpen}
  <div
    class="sl-backdrop"
    data-testid="session-limit-backdrop"
    role="dialog"
    aria-modal="true"
    aria-labelledby="session-limit-title"
  >
    <div class="sl-dialog">
      <h2 id="session-limit-title">{$_("login.sessionLimit.title")}</h2>
      <p>{$_("login.sessionLimit.intro", { max: maxSessions })}</p>
      <p class="hint" data-testid="session-limit-hint">
        {$_("login.sessionLimit.selectHint", { count: neededKills })}
      </p>
      <ul class="sl-list">
        {#each activeSessions as s (s.id)}
          <li class="sl-item">
            <label>
              <input
                type="checkbox"
                checked={killSelected.has(s.id)}
                on:change={() => toggleKill(s.id)}
                data-testid="session-kill-checkbox"
              />
              <span class="sl-meta">{sessionMeta(s)}</span>
            </label>
          </li>
        {/each}
      </ul>
      <div class="sl-actions">
        <button
          type="button"
          class="sl-cancel"
          on:click={cancelSessionLimit}
          disabled={submitting}
          data-testid="session-limit-cancel"
        >
          {$_("login.sessionLimit.cancel")}
        </button>
        <button
          type="button"
          class="sl-confirm"
          on:click={confirmKill}
          disabled={!canConfirmKill}
          data-testid="session-limit-confirm"
        >
          {submitting
            ? $_("login.signingIn")
            : $_("login.sessionLimit.killContinue")}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .login {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 1rem;
    /* iOS viewport-fit=cover: keep the card out from under the status bar and
       home indicator. env() is 0 elsewhere, so 1rem is preserved. */
    padding-top: max(1rem, env(safe-area-inset-top));
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }
  .card {
    width: 100%;
    max-width: 360px;
    background: #111827;
    border: 1px solid #374151;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
  }
  h1 {
    margin: 0 0 0.25rem;
    font-size: 1.5rem;
  }
  .sub {
    margin: 0 0 1rem;
    color: #9ca3af;
  }
  .repair-notice {
    margin: 0 0 1rem;
    padding: 0.75rem 0.9rem;
    background: #064e3b;
    border: 1px solid #10b981;
    border-radius: 8px;
    color: #d1fae5;
    font-size: 0.875rem;
  }
  .repair-notice strong {
    display: block;
    margin-bottom: 0.35rem;
    color: #ecfdf5;
  }
  .repair-notice p {
    margin: 0;
  }
  form {
    display: grid;
    gap: 0.75rem;
  }
  label {
    display: grid;
    gap: 0.25rem;
    font-size: 0.875rem;
    color: #d1d5db;
  }
  input {
    padding: 0.6rem 0.75rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 8px;
    font-size: 1rem;
  }
  input:focus {
    outline: 2px solid #10b981;
    outline-offset: 1px;
  }
  button {
    padding: 0.6rem 0.75rem;
    background: #10b981;
    color: #04221a;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
  }
  /* "Stay signed in": checkbox beside its text, overriding the stacked-grid
     label layout used by the email/password fields. */
  .remember {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    cursor: pointer;
  }
  .remember input {
    width: auto;
    margin-top: 0.15rem;
    padding: 0;
    flex: none;
    accent-color: #10b981;
  }
  .remember small {
    display: block;
    color: #9ca3af;
    font-size: 0.8rem;
  }
  .error {
    margin: 0;
    color: #fca5a5;
    font-size: 0.875rem;
  }
  .text-link {
    background: none;
    border: none;
    color: #9ca3af;
    font-size: 0.85rem;
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
    justify-self: start;
  }
  .text-link:hover {
    color: #d1d5db;
  }
  .forgot {
    display: inline-block;
    margin-top: 0.9rem;
    color: #6ee7b7;
    font-size: 0.85rem;
    text-decoration: none;
  }
  .forgot:hover {
    text-decoration: underline;
  }
  /* Concurrent-session limit modal (mirrors Profile's .pw-dialog). */
  .sl-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: grid;
    place-items: center;
    padding: 1rem;
    /* Keep a tall dialog clear of the status bar / home indicator. */
    padding-top: max(1rem, env(safe-area-inset-top));
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
    z-index: 50;
  }
  .sl-dialog {
    width: 100%;
    max-width: 420px;
    background: #111827;
    border: 1px solid #374151;
    border-radius: 12px;
    padding: 1.25rem;
    display: grid;
    gap: 0.75rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  }
  .sl-dialog h2 {
    margin: 0;
    font-size: 1.1rem;
  }
  .sl-dialog p {
    margin: 0;
    color: #d1d5db;
    font-size: 0.9rem;
  }
  .sl-dialog .hint {
    color: #fcd34d;
    font-size: 0.8rem;
  }
  .sl-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0.4rem;
    max-height: 50vh;
    overflow-y: auto;
  }
  .sl-item label {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.5rem 0.6rem;
    background: #1f2937;
    border: 1px solid #374151;
    border-radius: 8px;
    cursor: pointer;
  }
  .sl-item input {
    margin-top: 0.15rem;
    accent-color: #10b981;
  }
  /* The product owner asked for tiny, muted session metadata. */
  .sl-meta {
    font-size: 0.7rem;
    line-height: 1.3;
    color: #9ca3af;
    word-break: break-word;
  }
  .sl-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
  }
  .sl-actions button {
    font-size: 0.9rem;
    padding: 0.5rem 0.8rem;
  }
  .sl-cancel {
    background: #374151;
    color: #e5e7eb;
  }
  .sl-confirm:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
